/**
 * 갤러리 표준 컨트롤러 — layout 적용 + 라이트박스.
 *
 * [data-fw-gallery] (권장) 또는 [data-fiket-gallery] / .gallery-grid (레거시 fallback)
 * 안의 <img> 클릭 시 전체 화면 overlay 열기.
 * 좌우 화살표로 prev/next, X 또는 배경 클릭으로 닫기. 디자이너가 자체 라이트박스를
 * 이미 구현했다면 (#lightboxOverlay 가 있거나 자체 click 리스너 부착) 그대로 두고 skip.
 *
 * Layout 적용: settings.galleryLayout (grid-2/grid-3/grid-2-1/carousel/filmstrip/highlight)
 * 에 따라 grid 컨테이너와 자식 img 의 style 을 덮어씌움. SSR 결과의 hardcoded 마크업과
 * 무관하게 사용자 설정이 즉시 반영되도록 함.
 */
export const GALLERY_CONTROLLER_SCRIPT = `
<script data-fw-gallery-controller>
(function () {
  // 디자이너가 자체 라이트박스를 구현했으면 완전 skip
  if (document.getElementById('lightboxOverlay') || document.querySelector('[data-fw-gallery-skip]')) return;

  var grid = document.querySelector('[data-fw-gallery]')
          || document.querySelector('[data-fiket-gallery]')
          || document.querySelector('.gallery-grid');
  if (!grid) return;

  var imgs = Array.prototype.slice.call(grid.querySelectorAll('img'));
  if (imgs.length === 0) return;

  // ── 레이아웃 적용 ──
  // 이미지의 실제 부모를 grid 컨테이너로 사용 — section[data-fw-gallery] 같은 외곽 wrapper 에
  // grid 스타일이 적용돼 타이틀까지 column 으로 잡히는 사고를 방지.
  // (모든 표준 layout 에서 imgs 는 동일 부모를 가짐.)
  var gridEl = (imgs[0] && imgs[0].parentElement) || grid;

  var LAYOUT = {{ settings.galleryLayout | default: 'grid-3' | json }};
  // 이미지별 초점(object-position). images 와 같은 index 로 정렬. 미설정이면 '' → center 유지.
  var POSITIONS = {{ gallery.imagePositions | json }};
  if (!POSITIONS || Object.prototype.toString.call(POSITIONS) !== '[object Array]') POSITIONS = [];

  // 사진 사이 작은 gap (px). 그리드/필름 모두 동일 톤.
  var GRID_GAP = '6px';

  function applyLayout(g, items, layout) {
    // 공통 reset — OLD baked-in 인라인 style 도 깔끔히 제거
    g.style.display = '';
    g.style.gridTemplateColumns = '';
    g.style.gap = '';
    g.style.overflowX = '';
    g.style.scrollSnapType = '';
    items.forEach(function (im, i) {
      im.style.gridColumn = '';
      im.style.flex = '';
      im.style.scrollSnapAlign = '';
      im.style.aspectRatio = '';
      im.style.height = '';
      im.style.width = '';
      // 초점 적용 — SSR data-index 우선(재정렬돼도 index 정합), 없으면 순회 index.
      var pi = im.getAttribute('data-index');
      var pos = POSITIONS[pi != null ? Number(pi) : i];
      im.style.objectPosition = pos || '';
    });

    if (layout === 'grid-2-1') {
      g.style.display = 'grid';
      g.style.gridTemplateColumns = 'repeat(2, 1fr)';
      g.style.gap = GRID_GAP;
      items.forEach(function (im) { im.style.width = '100%'; im.style.aspectRatio = '1 / 1'; });
      if (items[0]) { items[0].style.gridColumn = '1 / -1'; items[0].style.aspectRatio = '16 / 9'; }
    } else if (layout === 'carousel') {
      g.style.display = 'flex';
      g.style.overflowX = 'auto';
      g.style.scrollSnapType = 'x mandatory';
      g.style.gap = '0';
      items.forEach(function (im) {
        im.style.flex = '0 0 100%';
        im.style.scrollSnapAlign = 'center';
        im.style.width = '100%';
        im.style.aspectRatio = '4 / 3';
      });
    } else if (layout === 'filmstrip') {
      g.style.display = 'flex';
      g.style.overflowX = 'auto';
      g.style.gap = GRID_GAP;
      items.forEach(function (im) {
        im.style.flex = '0 0 auto';
        im.style.height = '120px';
        im.style.width = 'auto';
        im.style.aspectRatio = 'auto';
      });
    } else if (layout === 'highlight') {
      g.style.display = 'grid';
      g.style.gridTemplateColumns = 'repeat(3, 1fr)';
      g.style.gap = GRID_GAP;
      items.forEach(function (im) { im.style.width = '100%'; im.style.aspectRatio = '1 / 1'; });
      if (items[0]) { items[0].style.gridColumn = '1 / -1'; items[0].style.aspectRatio = '16 / 9'; }
    } else if (layout === 'grid-3') {
      g.style.display = 'grid';
      g.style.gridTemplateColumns = 'repeat(3, 1fr)';
      g.style.gap = GRID_GAP;
      items.forEach(function (im) { im.style.width = '100%'; im.style.aspectRatio = '1 / 1'; });
    } else {
      // grid-2 (default fallback) — masonry/single-column 같은 옛 값도 여기로 떨어짐
      g.style.display = 'grid';
      g.style.gridTemplateColumns = 'repeat(2, 1fr)';
      g.style.gap = GRID_GAP;
      items.forEach(function (im) { im.style.width = '100%'; im.style.aspectRatio = '1 / 1'; });
    }
  }

  applyLayout(gridEl, imgs, LAYOUT);

  var idx = 0;
  var ov = null;
  var img = null;
  var counter = null;

  function makeOverlay() {
    ov = document.createElement('div');
    ov.setAttribute('data-fw-gallery-overlay', '');
    ov.style.cssText = 'display:none;position:fixed;inset:0;background:rgba(0,0,0,0.92);z-index:10001;align-items:center;justify-content:center;';
    img = document.createElement('img');
    img.style.cssText = 'max-width:94vw;max-height:84vh;object-fit:contain;display:block;';
    ov.appendChild(img);
    var close = document.createElement('button');
    close.type = 'button';
    close.textContent = '\\u00d7';
    close.style.cssText = 'position:absolute;top:16px;right:20px;font-size:32px;color:#fff;background:none;border:0;cursor:pointer;line-height:1;';
    close.addEventListener('click', hide);
    ov.appendChild(close);
    var prev = document.createElement('button');
    prev.type = 'button';
    prev.textContent = '\\u2039';
    prev.style.cssText = 'position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:48px;color:#fff;background:none;border:0;cursor:pointer;padding:8px 16px;';
    prev.addEventListener('click', function (e) { e.stopPropagation(); go(-1); });
    ov.appendChild(prev);
    var next = document.createElement('button');
    next.type = 'button';
    next.textContent = '\\u203A';
    next.style.cssText = 'position:absolute;right:12px;top:50%;transform:translateY(-50%);font-size:48px;color:#fff;background:none;border:0;cursor:pointer;padding:8px 16px;';
    next.addEventListener('click', function (e) { e.stopPropagation(); go(1); });
    ov.appendChild(next);
    counter = document.createElement('div');
    counter.style.cssText = 'position:absolute;bottom:16px;left:50%;transform:translateX(-50%);color:#fff;font-size:14px;opacity:0.8;';
    ov.appendChild(counter);
    ov.addEventListener('click', function (e) { if (e.target === ov) hide(); });
    document.body.appendChild(ov);
  }
  function show(i) {
    if (!ov) makeOverlay();
    // 이미 열려 있으면(화살표 prev/next 로 재호출) 다시 lock 하지 않는다.
    // lock 은 스택 기반이라 열 때마다 쌓으면 hide()의 unlock 1회로 균형이 안 맞아
    // 닫은 뒤에도 배경 스크롤이 잠긴 채 남는다.
    var wasOpen = ov.style.display !== 'none';
    idx = (i + imgs.length) % imgs.length;
    img.src = imgs[idx].src;
    img.alt = imgs[idx].alt || '';
    counter.textContent = (idx + 1) + ' / ' + imgs.length;
    ov.style.display = 'flex';
    if (wasOpen) return;
    // 라이트박스는 단일 이미지라 내부 스크롤이 없음 → 전역 차단(배경 스크롤 누수 방지)
    if (window.__fwScrollLock) window.__fwScrollLock.lock();
    else document.body.style.overflow = 'hidden';
  }
  function hide() {
    if (!ov || ov.style.display === 'none') return; // 이미 닫혀 있으면 중복 unlock 방지
    ov.style.display = 'none';
    if (window.__fwScrollLock) window.__fwScrollLock.unlock();
    else document.body.style.overflow = '';
  }
  function go(d) { show(idx + d); }

  imgs.forEach(function (el, i) {
    el.style.cursor = 'pointer';
    el.addEventListener('click', function (e) { e.preventDefault(); show(i); });
  });

  document.addEventListener('keydown', function (e) {
    if (!ov || ov.style.display === 'none') return;
    if (e.key === 'Escape') hide();
    else if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'ArrowRight') go(1);
  });
})();
</script>
`;
//# sourceMappingURL=gallery.js.map